import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axios';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminNote, setAdminNote] = useState({});

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'orders':
          const ordersRes = await axios.get('/api/admin/orders');
          setOrders(ordersRes.data);
          break;
        case 'users':
          const usersRes = await axios.get('/api/admin/users');
          setUsers(usersRes.data);
          break;
        case 'stats':
          const statsRes = await axios.get('/api/admin/stats');
          setStats(statsRes.data);
          break;
        case 'transactions':
          const transactionsRes = await axios.get('/api/admin/transactions');
          setTransactions(transactionsRes.data);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(err);
      alert('Error fetching data');
    }
    setLoading(false);
  };

  const handleFulfill = async (orderId) => {
    if (!window.confirm('Fulfill this order? Gift card codes will be generated and sent to the user.')) {
      return;
    }
    try {
      await axios.put(`/api/admin/orders/${orderId}/fulfill`, {
        adminNotes: adminNote[orderId] || ''
      });
      alert('Order fulfilled successfully! Gift cards delivered to user.');
      fetchData();
    } catch (err) {
      console.error(err);
      alert('Error fulfilling order');
    }
  };

  const handleReject = async (orderId) => {
    try {
      const note = adminNote[orderId] || prompt('Enter rejection reason:');
      if (!note) return;
      
      await axios.put(`/api/admin/orders/${orderId}/reject`, {
        adminNotes: note
      });
      alert('Order rejected successfully');
      fetchData();
    } catch (err) {
      console.error(err);
      alert('Error rejecting order');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'orders'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              Pending Orders
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'users'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'stats'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              Statistics
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'transactions'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              Transactions
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4">Pending Orders</h2>
                  {orders.length === 0 ? (
                    <p className="text-gray-600">No pending orders</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left">Order ID</th>
                            <th className="px-4 py-2 text-left">User</th>
                            <th className="px-4 py-2 text-left">Items</th>
                            <th className="px-4 py-2 text-left">Total</th>
                            <th className="px-4 py-2 text-left">Crypto</th>
                            <th className="px-4 py-2 text-left">Status</th>
                            <th className="px-4 py-2 text-left">Date</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order) => (
                            <tr key={order._id} className="border-t">
                              <td className="px-4 py-3 text-sm">{order._id.slice(-8)}</td>
                              <td className="px-4 py-3 text-sm">
                                {order.user?.email || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {order.items.length} item(s)
                              </td>
                              <td className="px-4 py-3 text-sm">
                                ${order.totalUSD.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {order.cryptoAmount.toFixed(8)} {order.cryptocurrency}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span
                                  className={`px-2 py-1 rounded text-xs ${
                                    order.status === 'pending_fulfillment'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : order.status === 'fulfilled'
                                      ? 'bg-green-100 text-green-800'
                                      : order.status === 'rejected'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {order.status.replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {new Date(order.createdAt).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {order.status === 'pending_fulfillment' && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleFulfill(order._id)}
                                      className="bg-green-500 text-white px-3 py-1 rounded text-xs hover:bg-green-600"
                                    >
                                      Fulfill Order
                                    </button>
                                    <button
                                      onClick={() => handleReject(order._id)}
                                      className="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                )}
                                {order.status !== 'pending_fulfillment' && (
                                  <span className="text-gray-400 text-xs">
                                    {order.status === 'fulfilled' ? 'Fulfilled' : order.status === 'rejected' ? 'Rejected' : order.status}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4">All Users</h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Email</th>
                          <th className="px-4 py-2 text-left">Role</th>
                          <th className="px-4 py-2 text-left">Wallets</th>
                          <th className="px-4 py-2 text-left">Total Balance USD</th>
                          <th className="px-4 py-2 text-left">Registered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u._id} className="border-t">
                            <td className="px-4 py-3 text-sm">{u.email}</td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded text-xs ${
                                  u.role === 'admin'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {u.role}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {u.platformWallets?.length || 0}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              $
                              {u.platformWallets
                                ?.reduce((sum, w) => sum + (w.balanceUSD || 0), 0)
                                .toFixed(2) || '0.00'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {new Date(u.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-gray-600 text-sm font-medium mb-2">Total Users</h3>
                  <p className="text-3xl font-bold text-blue-600">{stats.totalUsers || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-gray-600 text-sm font-medium mb-2">Total Orders</h3>
                  <p className="text-3xl font-bold text-green-600">{stats.totalOrders || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-gray-600 text-sm font-medium mb-2">Pending Orders</h3>
                  <p className="text-3xl font-bold text-yellow-600">
                    {stats.pendingOrders || 0}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-gray-600 text-sm font-medium mb-2">Total Revenue</h3>
                  <p className="text-3xl font-bold text-purple-600">
                    ${stats.totalRevenue?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4">All Transactions</h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">User</th>
                          <th className="px-4 py-2 text-left">Type</th>
                          <th className="px-4 py-2 text-left">Crypto</th>
                          <th className="px-4 py-2 text-left">Amount</th>
                          <th className="px-4 py-2 text-left">Amount USD</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx) => (
                          <tr key={tx._id} className="border-t">
                            <td className="px-4 py-3 text-sm">{tx.user?.email || 'N/A'}</td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded text-xs ${
                                  tx.type === 'deposit'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {tx.type}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">{tx.cryptocurrency}</td>
                            <td className="px-4 py-3 text-sm">
                              {tx.amount.toFixed(8)} {tx.cryptocurrency}
                            </td>
                            <td className="px-4 py-3 text-sm">${tx.amountUSD.toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded text-xs ${
                                  tx.status === 'confirmed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {tx.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {new Date(tx.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
